"use client";

/**
 * Reusable confirmation modal for delete actions
 * Replaces browser confirm() with a styled FetchModal
 */

import type React from "react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import FetchModal from "@/fetch-ui/FetchModal";

interface ConfirmDeleteModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal is closed without confirming */
  onClose: () => void;
  /** Called when the user confirms the delete action */
  onConfirm: () => void;
  /** Modal title (e.g., "Delete Activity") */
  title: string;
  /** Description text explaining what will be deleted */
  description: string;
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
}

/** Confirmation modal for destructive delete actions */
export function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  isDeleting = false,
}: ConfirmDeleteModalProps): React.ReactElement {
  return (
    <FetchModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="auto"
      className="max-w-sm"
      bottomBar={
        <div className="flex justify-end gap-3">
          <FetchButton variant="outline" size="sm" onClick={onClose} disabled={isDeleting}>
            Cancel
          </FetchButton>
          <FetchButton variant="primary" color="destructive" size="sm" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </FetchButton>
        </div>
      }
    >
      <p className="text-sm font-medium text-gray-600">{description}</p>
    </FetchModal>
  );
}
